/**
 * Shared types for Document Folders feature
 *
 * Document folders organize company documents and employee documents into
 * hierarchical folder structures. Company folders are managed by admins,
 * while employee folders are personal to each employment record.
 */
import { z } from "zod";
// ===== ENUMS =====
export const DocumentFolderOwnerTypeSchema = z.enum(["COMPANY", "EMPLOYEE"]);
// ===== INPUT SCHEMAS =====
export const CreateDocumentFolderInputSchema = z.object({
    name: z.string().min(1).max(255),
    ownerType: DocumentFolderOwnerTypeSchema,
    employmentId: z.uuid().optional(), // Required when ownerType is EMPLOYEE
    parentId: z.uuid().optional(), // null = root level
}).refine((data) => data.ownerType !== "EMPLOYEE" || data.employmentId !== undefined, { message: "employmentId is required when ownerType is EMPLOYEE", path: ["employmentId"] });
export const UpdateDocumentFolderInputSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    parentId: z.uuid().nullable().optional(), // Can move to root with null
});
export const ListDocumentFoldersInputSchema = z.object({
    ownerType: DocumentFolderOwnerTypeSchema.optional(), // Filter by owner type
    employmentId: z.uuid().optional(), // Filter by employment (for employee folders)
    parentId: z.uuid().nullable().optional(), // Filter by parent (null = root level)
    flat: z.boolean().optional().default(false), // If true, return flat list; if false, return tree
});
// ===== RESPONSE SCHEMAS =====
export const DocumentFolderResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    ownerType: DocumentFolderOwnerTypeSchema,
    companyId: z.uuid(),
    employmentId: z.uuid().nullable(),
    parentId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    createdBy: z.uuid(),
    updatedAt: z.iso.datetime(),
});
// Recursive tree node schema
export const FolderTreeNodeSchema = z.lazy(() => DocumentFolderResponseSchema.extend({
    children: z.array(FolderTreeNodeSchema),
}));
